import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Facebook, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { SITE_INFO, NAV_LINKS } from "@/lib/constants";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message);
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#1A3C34] text-white/90">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          <div>
            <h3 className="font-heading text-2xl font-semibold text-white mb-4">
              Serenity Village
            </h3>
            <p className="font-body text-sm text-white/60 leading-relaxed mb-6">
              Luxury glamping, cabins, and camping near Lake Texoma. Where nature,
              adventure, and relaxation meet.
            </p>
            <a
              href={SITE_INFO.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-200"
            >
              <Facebook size={18} />
              <span className="text-sm font-body">Facebook</span>
            </a>
          </div>

          <div>
            <h4 className="font-accent text-xs tracking-[0.2em] uppercase text-white/40 mb-6">
              Quick Links
            </h4>
            <nav className="flex flex-col gap-3">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="font-body text-sm text-white/60 hover:text-white transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="font-accent text-xs tracking-[0.2em] uppercase text-white/40 mb-6">
              Contact
            </h4>
            <div className="flex flex-col gap-4">
              <a
                href={`tel:${SITE_INFO.phoneRaw}`}
                data-testid="footer-phone"
                className="flex items-start gap-3 text-sm font-body text-white/60 hover:text-white transition-colors duration-200"
              >
                <Phone size={16} className="mt-0.5 shrink-0" />
                {SITE_INFO.phone}
              </a>
              <a
                href={`mailto:${SITE_INFO.email}`}
                data-testid="footer-email"
                className="flex items-start gap-3 text-sm font-body text-white/60 hover:text-white transition-colors duration-200"
              >
                <Mail size={16} className="mt-0.5 shrink-0" />
                {SITE_INFO.email}
              </a>
              <div className="flex items-start gap-3 text-sm font-body text-white/60">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                {SITE_INFO.address}
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-accent text-xs tracking-[0.2em] uppercase text-white/40 mb-6">
              Stay Updated
            </h4>
            <p className="font-body text-sm text-white/60 mb-4">
              Get exclusive deals and resort updates.
            </p>
            <form onSubmit={handleNewsletter} className="flex gap-2">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-newsletter-input"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 font-body text-sm"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="bg-accent hover:bg-accent/90 text-white shrink-0"
                size="icon"
              >
                <ArrowRight size={16} />
              </Button>
            </form>
          </div>
        </div>

        <Separator className="my-8 bg-white/10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-white/40">
            &copy; {new Date().getFullYear()} Texoma Resorts / Serenity Village. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/policies"
              className="font-body text-xs text-white/40 hover:text-white/70 transition-colors duration-200"
            >
              Policies & Terms
            </Link>
            <Link
              to="/about"
              className="font-body text-xs text-white/40 hover:text-white/70 transition-colors duration-200"
            >
              About
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
